import React, { useState } from "react";
import ActionDropdown from "./ActionDropdown";
import SortDropdown from "./SortDropdown";
import FlightLIsting from "./FlightLIsting";
import YourRequestFilter from "./YourRequestFilter";
import Pagination from "../../components/Pagination";
import { useLoadRequests } from "./useLoadRequests";

interface YourRequestProps {
  onShowHistory: () => void;
}

interface FilterState {
  status?: string;
}

const YourRequest: React.FC<YourRequestProps> = ({ onShowHistory }) => {
  const {
    flightsDetails,
    loading,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    setStatus,
    currentPage,
    setCurrentPage,
    pageSize,
    clearSelectAll,
    fetchLoadRequests,
    handlePageChange,
    handlePageSizeChange,
  } = useLoadRequests();

  // UI-only state (toolbar dropdowns + the in-flight bulk action).
  const [actionIsOpen, setActionIsOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  // Pin state of the current selection (reported up from FlightLIsting) so the
  // Actions menu can show "Pin" vs "Unpin". null = nothing selected.
  const [selectedPinned, setSelectedPinned] = useState<boolean | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    status: "",
  });

  const handleFilterChange = (filters: FilterState) => {
    setStatus(filters.status || "");
    setSelectedFilters(filters);
    setShowFilter(false);
    // Reset to the first page on any status change so we don't land on a now
    // out-of-range page (e.g. switching to a filter with fewer results).
    setCurrentPage(1);
  };

  const handleActionClick = (action: string) => {
    setSelectedAction(action);
    // Reset the action after processing so the same action can fire again.
    setTimeout(() => setSelectedAction(null), 100);
  };

  const toggleFilter = () => setShowFilter((prev) => !prev);

  return (
    <>
      <div className="-m-6">
        <section className="flex flex-wrap gap-3 sm:gap-4 items-center justify-between px-6 pt-6 pb-4">
          <p className="text-[#333333] text-lg font-semibold">Enquiries</p>
          <div className="flex flex-wrap items-center gap-2">
            {/* Refresh Button */}
            <button
              onClick={() => fetchLoadRequests()}
              disabled={loading}
              className="flex items-center gap-2 p-3 bg-[#FFFFFF] rounded-lg disabled:opacity-60"
              title="Refresh"
              aria-label="Refresh"
            >
              <i
                className={`st-reload text-[#000000] ${loading ? "animate-spin" : ""}`}
              ></i>
            </button>
            {/* Filters Button */}
            <div className="relative">
              <button
                onClick={toggleFilter}
                className="flex items-center gap-2 p-3 bg-[#FFFFFF] rounded-lg"
              >
                <i className="st-filter text-[#000000]"></i>
                <p className="font-medium text-[#000000] text-sm">Filters</p>
              </button>
              {showFilter && (
                <div className="absolute right-0 max-sm:left-0 max-sm:right-auto origin-top-right mt-2 max-w-[calc(100vw-1rem)] z-50">
                  <YourRequestFilter
                    activeFilters={selectedFilters}
                    onClose={() => setShowFilter(false)}
                    onFilterChange={handleFilterChange}
                  />
                </div>
              )}
            </div>
            <button
              onClick={onShowHistory}
              className="flex items-center gap-2 p-3 bg-[#FFFFFF] rounded-lg"
            >
              <i className="st-previous text-[#000000]"></i>
              <p className="font-medium text-[#000000] text-sm">History</p>
            </button>
            <div className="relative inline-block">
              <button
                id="dropdownDefaultButton"
                onClick={() => setActionIsOpen((prev) => !prev)}
                className="flex items-center gap-2 p-3 bg-[#FFFFFF] rounded-lg"
                type="button"
              >
                <i className="st-more text-[#000000] text-xs"></i>
                <p className="font-medium text-[#000000] text-sm">Actions</p>
              </button>
              <ActionDropdown
                actionIsOpen={actionIsOpen}
                setActionIsOpen={setActionIsOpen}
                onActionClick={handleActionClick}
                isPinned={selectedPinned}
              />
            </div>
            <div className="relative">
              <button
                className="flex items-center gap-2 p-4 bg-[#FFFFFF] rounded-lg"
                type="button"
                onClick={() => setIsSortDropdownOpen((prev) => !prev)}
              >
                <i className="st-options text-[#000000] text-xs"></i>
              </button>

              <SortDropdown
                isOpen={isSortDropdownOpen}
                setIsOpen={setIsSortDropdownOpen}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </div>
          </div>
        </section>
        <div className="px-6">
          <FlightLIsting
            flights={flightsDetails}
            action={selectedAction}
            clearSelectAll={clearSelectAll}
            fetchLoadRequests={fetchLoadRequests}
            loading={loading}
            onSelectionChange={setSelectedPinned}
          />
        </div>
        <section>
          <Pagination
            currentPage={currentPage}
            totalCount={flightsDetails.totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showTotal
          />
        </section>
      </div>
    </>
  );
};

export default YourRequest;
